import { useFormContext } from 'react-hook-form';
import {
    Checkbox,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Divider,
    FormControlLabel,
} from '@mui/material';

import ArrowDownBigger from '@images/svg-icons/arrowDownBigger.svg';
import { addPositiveSign, getLabelFallback, isMobile, returnValueIf } from '@utils/utils';
import { IContactLens } from '@interfaces/contactsInterfaces';
import FormColumn from './FormColumn';

type DifferentProductPrescriptionFormProps = {
    rightCl?: IContactLens;
    leftCl?: IContactLens;
    defaultBoxesValue: string;
    boxesValueOptions: Array<string>;
};

const DifferentProductPrescriptionForm = ({
    rightCl,
    leftCl,
    defaultBoxesValue,
    boxesValueOptions,
}: DifferentProductPrescriptionFormProps) => {
    const { register, watch } = useFormContext();

    const watchLeftEnabled = watch('rxLeftEye');
    const watchRightEnabled = watch('rxRightEye');

    const product = rightCl || leftCl;
    const isProductEnabled = !!rightCl ? watchRightEnabled : watchLeftEnabled;

    return (
        <div className="saved-prescription-other-product">
            <div className="saved-prescription-other-product-title">
                {getLabelFallback(
                    !!rightCl
                        ? 'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_EYE_TYPE_RIGHT_LONG_LABEL_DIF'
                        : 'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_EYE_TYPE_LEFT_LONG_LABEL_DIF'
                )}
            </div>

            <div className="saved-prescription-other-product-details">
                <img
                    className="saved-prescription-other-product-details-image ls-is-cached lazyloaded mCS_img_loaded"
                    alt={`image for ${product?.model}`}
                    width="90px"
                    data-original={product?.thumbnail}
                    src={product?.thumbnail}
                />

                <div className="saved-prescription-other-product-details-description">
                    <div className="pack-name">{product?.model}</div>
                    <div className="pack-brand">{product?.brand}</div>
                </div>

                <Divider />

                <Accordion className="show-presc-pack-btn">
                    <AccordionSummary
                        className="show-presc-btn-content"
                        data-analytics_available_call="0"
                    >
                        <span className="show-presc-btn-label">
                            {getLabelFallback('CONTACT_LENSES_SHOW_SAVED_PRESCRIPTION')}
                        </span>
                        <span className="show-presc-btn-icon">
                            <ArrowDownBigger />
                        </span>
                    </AccordionSummary>

                    <AccordionDetails>
                        <div className="form-container saved-prescription">
                            {/* EYE CHECK */}
                            <div className="eye-column param-column">
                                <div className="param-label eye-label">
                                    <label>
                                        {getLabelFallback(
                                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_EYE_LABEL'
                                        )}
                                    </label>
                                </div>

                                <div className="presc-param-select">
                                    {getLabelFallback(
                                        !!rightCl
                                            ? 'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_EYE_TYPE_RIGHT_LONG_LABEL'
                                            : 'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_EYE_TYPE_LEFT_LONG_LABEL'
                                    )}
                                </div>
                            </div>

                            {/* POWER PARAM */}
                            <FormColumn
                                attribute="power"
                                label={
                                    isMobile
                                        ? getLabelFallback(
                                              'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_POWER_LABEL_MOBILE'
                                          )
                                        : getLabelFallback(
                                              'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_POWER_LABEL'
                                          )
                                }
                                left={returnValueIf(
                                    {
                                        enabled: watchLeftEnabled,
                                        name: 'rxLeftPower',
                                        values: addPositiveSign(leftCl?.attributes?.power),
                                    },
                                    !!leftCl
                                )}
                                right={returnValueIf(
                                    {
                                        enabled: watchRightEnabled,
                                        name: 'rxRightPower',
                                        values: addPositiveSign(rightCl?.attributes?.power),
                                    },
                                    !!rightCl
                                )}
                            />

                            {/* BC PARAM */}
                            <FormColumn
                                attribute="bc"
                                label={getLabelFallback(
                                    'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_BC_LABEL'
                                )}
                                left={returnValueIf(
                                    {
                                        enabled: watchLeftEnabled,
                                        name: 'rxLeftBc',
                                        values: leftCl?.attributes?.baseCurve,
                                    },
                                    !!leftCl
                                )}
                                right={returnValueIf(
                                    {
                                        enabled: watchRightEnabled,
                                        name: 'rxRightBc',
                                        values: rightCl?.attributes?.baseCurve,
                                    },
                                    !!rightCl
                                )}
                            />

                            {/* DIA PARAM */}
                            <FormColumn
                                attribute="dia"
                                label={getLabelFallback(
                                    'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_DIA_LABEL'
                                )}
                                left={returnValueIf(
                                    {
                                        enabled: watchLeftEnabled,
                                        name: 'rxLeftDia',
                                        values: leftCl?.attributes?.diameter,
                                    },
                                    !!leftCl
                                )}
                                right={returnValueIf(
                                    {
                                        enabled: watchRightEnabled,
                                        name: 'rxRightDia',
                                        values: rightCl?.attributes?.diameter,
                                    },
                                    !!rightCl
                                )}
                            />

                            {/* CYL PARAM */}
                            <FormColumn
                                attribute="cyl"
                                label={getLabelFallback(
                                    'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_CYL_LABEL'
                                )}
                                left={returnValueIf(
                                    {
                                        enabled: watchLeftEnabled,
                                        name: 'rxLeftCyl',
                                        values: leftCl?.attributes?.cylinder,
                                    },
                                    !!leftCl &&
                                        !(
                                            leftCl?.attributes.cylinder?.length === 1 &&
                                            parseFloat(leftCl.attributes.cylinder[0]) === 0
                                        )
                                )}
                                right={returnValueIf(
                                    {
                                        enabled: watchRightEnabled,
                                        name: 'rxRightCyl',
                                        values: rightCl?.attributes?.cylinder,
                                    },
                                    !!rightCl &&
                                        !(
                                            rightCl?.attributes.cylinder?.length === 1 &&
                                            parseFloat(rightCl.attributes.cylinder[0]) === 0
                                        )
                                )}
                            />

                            {/* AXIS PARAM */}
                            <FormColumn
                                attribute="axis"
                                label={getLabelFallback(
                                    'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_AXIS_LABEL'
                                )}
                                left={returnValueIf(
                                    {
                                        enabled: watchLeftEnabled,
                                        name: 'rxLeftAxis',
                                        values: leftCl?.attributes?.axis,
                                    },
                                    !!leftCl &&
                                        !(
                                            leftCl?.attributes.axis.length === 1 &&
                                            parseFloat(leftCl.attributes.axis[0]) === 0
                                        )
                                )}
                                right={returnValueIf(
                                    {
                                        enabled: watchRightEnabled,
                                        name: 'rxRightAxis',
                                        values: rightCl?.attributes?.axis,
                                    },
                                    !!rightCl &&
                                        !(
                                            rightCl?.attributes.axis.length === 1 &&
                                            parseFloat(rightCl.attributes.axis[0]) === 0
                                        )
                                )}
                            />

                            {/* ADD PARAM */}
                            <FormColumn
                                attribute="add"
                                label={getLabelFallback(
                                    'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_ADD_LABEL'
                                )}
                                left={returnValueIf(
                                    {
                                        enabled: watchLeftEnabled,
                                        name: 'rxLeftAdd',
                                        values: leftCl?.attributes?.add,
                                    },
                                    !!leftCl
                                )}
                                right={returnValueIf(
                                    {
                                        enabled: watchRightEnabled,
                                        name: 'rxRightAdd',
                                        values: rightCl?.attributes?.add,
                                    },
                                    !!rightCl
                                )}
                            />

                            {/* COLOR PARAM */}
                            <FormColumn
                                attribute="color"
                                label={getLabelFallback(
                                    'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_COLOR_LABEL'
                                )}
                                left={returnValueIf(
                                    {
                                        enabled: watchLeftEnabled,
                                        name: 'rxLeftColor',
                                        values: leftCl?.attributes?.color,
                                    },
                                    !!leftCl &&
                                        !(
                                            leftCl?.attributes?.color.length === 1 &&
                                            leftCl?.attributes?.color[0] == 'Clear'
                                        )
                                )}
                                right={returnValueIf(
                                    {
                                        enabled: watchRightEnabled,
                                        name: 'rxRightColor',
                                        values: rightCl?.attributes?.color,
                                    },
                                    !!rightCl &&
                                        !(
                                            rightCl?.attributes?.color.length === 1 &&
                                            rightCl?.attributes?.color[0] == 'Clear'
                                        )
                                )}
                            />

                            {/* DOMINANCE PARAM */}
                            <FormColumn
                                attribute="dominance"
                                label={getLabelFallback(
                                    'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_DOMINANCE_LABEL'
                                )}
                                left={returnValueIf(
                                    {
                                        enabled: watchLeftEnabled,
                                        name: 'rxLeftDominance',
                                        values: leftCl?.attributes?.dominance,
                                    },
                                    !!leftCl
                                )}
                                right={returnValueIf(
                                    {
                                        enabled: watchRightEnabled,
                                        name: 'rxRightDominance',
                                        values: rightCl?.attributes?.dominance,
                                    },
                                    !!rightCl
                                )}
                            />
                        </div>
                    </AccordionDetails>
                </Accordion>
            </div>

            <div className="saved-prescription-other-product-checkbox">
                <FormControlLabel
                    control={
                        <Checkbox
                            {...register(!!rightCl ? 'rxRightEye' : 'rxLeftEye')}
                            size="small"
                            inputProps={{
                                'aria-label': 'Check include product in purchase',
                            }}
                        />
                    }
                    label={getLabelFallback(
                        'CONTACT_LENSES_PRESCRIPTION_INCLUDE_PRODUCT_IN_PURCHASE_CHECKBOX'
                    )}
                />
            </div>

            {isProductEnabled && (
                <div className="saved-prescription-other-product-boxes form-container">
                    {/* BOXES PARAM */}
                    <FormColumn
                        attribute="boxes"
                        label={getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_BOXES_LABEL'
                        )}
                        left={returnValueIf(
                            {
                                enabled: watchLeftEnabled,
                                defaultValue: defaultBoxesValue,
                                name: 'leftQuantity',
                                values: boxesValueOptions,
                            },
                            !!leftCl
                        )}
                        right={returnValueIf(
                            {
                                enabled: watchRightEnabled,
                                defaultValue: defaultBoxesValue,
                                name: 'rightQuantity',
                                values: boxesValueOptions,
                            },
                            !!rightCl
                        )}
                    />
                </div>
            )}
            <input type="hidden" id="isDifferentProductsSelected" value="true" />
        </div>
    );
};

export default DifferentProductPrescriptionForm;
